import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import UserAvatar from '../components/UserAvatar';
import { 
  MessageCircle, 
  Inbox, 
  Mail, 
  Search, 
  ChevronRight,
  Clock,
  Check,
  CheckCheck,
  Loader2
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

export default function MessagesPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'inbox');
  const [conversations, setConversations] = useState([]);
  const [counts, setCounts] = useState({ inbox: 0, requests: 0 });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchConversations = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API}/messages/conversations?tab=${activeTab}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setConversations(data.conversations || []);
        setCounts(data.counts || { inbox: 0, requests: 0 });
      }
    } catch (err) {
      console.error('Error fetching conversations:', err);
    } finally {
      setLoading(false);
    }
  }, [token, activeTab]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  useEffect(() => {
    setSearchParams({ tab: activeTab });
  }, [activeTab, setSearchParams]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const openConversation = (conversationId) => {
    navigate(`/messages/${conversationId}`);
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return '';
    }
  };

  const filteredConversations = conversations.filter(conv => {
    if (!searchQuery) return true;
    const otherUser = conv.other_user || {};
    const searchLower = searchQuery.toLowerCase();
    return (
      (otherUser.name || '').toLowerCase().includes(searchLower) ||
      (otherUser.handle || '').toLowerCase().includes(searchLower) ||
      (conv.last_message_preview || '').toLowerCase().includes(searchLower)
    );
  });

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="p-8 text-center">
          <MessageCircle className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-xl font-semibold mb-2">Sign in to view messages</h2>
          <p className="text-muted-foreground mb-4">You need to be logged in to access your messages.</p>
          <Button onClick={() => navigate('/login')}>Sign In</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <MessageCircle className="w-8 h-8 text-primary" />
            <h1 className="text-2xl font-bold">Messages</h1>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="messages-search"
          />
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="mb-6">
          <TabsList className="w-full grid grid-cols-2">
            <TabsTrigger value="inbox" className="flex items-center gap-2" data-testid="inbox-tab">
              <Inbox className="w-4 h-4" />
              Inbox
              {counts.inbox > 0 && (
                <Badge variant="secondary" className="ml-1">{counts.inbox}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="requests" className="flex items-center gap-2" data-testid="requests-tab">
              <Mail className="w-4 h-4" />
              Requests
              {counts.requests > 0 && (
                <Badge variant="destructive" className="ml-1">{counts.requests}</Badge>
              )}
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Conversations List */}
        <Card className="divide-y divide-border" data-testid="conversations-list">
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-muted-foreground" />
              <p className="mt-2 text-muted-foreground">Loading conversations...</p>
            </div>
          ) : filteredConversations.length === 0 ? (
            <div className="p-8 text-center">
              {activeTab === 'inbox' ? (
                <>
                  <MessageCircle className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <h3 className="font-semibold mb-1">No conversations yet</h3>
                  <p className="text-sm text-muted-foreground">
                    Start a conversation by messaging an artist or fan!
                  </p>
                </>
              ) : (
                <>
                  <Mail className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <h3 className="font-semibold mb-1">No message requests</h3>
                  <p className="text-sm text-muted-foreground">
                    When someone you don't follow messages you, it will appear here.
                  </p>
                </>
              )}
            </div>
          ) : (
            filteredConversations.map((conv) => (
              <ConversationItem
                key={conv.conversation_id}
                conversation={conv}
                onClick={() => openConversation(conv.conversation_id)}
                formatTime={formatTime}
                isRequest={activeTab === 'requests'}
              />
            ))
          )}
        </Card>
      </div>
    </div>
  );
}

function ConversationItem({ conversation, onClick, formatTime, isRequest }) {
  const otherUser = conversation.other_user || {};
  const hasUnread = conversation.unread > 0;

  return (
    <button
      onClick={onClick}
      className={`w-full p-4 flex items-center gap-3 hover:bg-muted/50 transition-colors text-left ${
        hasUnread ? 'bg-primary/5' : ''
      }`}
      data-testid={`conversation-${conversation.conversation_id}`}
    >
      <div className="relative">
        <UserAvatar
          src={otherUser.avatar}
          name={otherUser.name || 'User'}
          size="lg"
        />
        {hasUnread && (
          <div className="absolute -top-1 -right-1 w-3 h-3 bg-primary rounded-full border-2 border-background" />
        )}
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <span className={`font-semibold truncate ${hasUnread ? 'text-foreground' : ''}`}>
            {otherUser.name || 'Unknown User'}
          </span>
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {formatTime(conversation.last_message_at)}
          </span>
        </div>
        
        <div className="flex items-center gap-2">
          {otherUser.handle && (
            <span className="text-xs text-muted-foreground">{otherUser.handle ? `@${otherUser.handle}` : ""}</span>
          )}
          {isRequest && (
            <Badge variant="outline" className="text-xs">Request</Badge>
          )}
        </div>
        
        <p className={`text-sm truncate mt-1 ${hasUnread ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
          {conversation.last_message_preview || 'No messages yet'}
        </p>
        
        {hasUnread && (
          <Badge variant="default" className="mt-1">{conversation.unread} new</Badge>
        )}
      </div>
      
      <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
    </button>
  );
}
